import { Stack, VStack } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import React, { useContext } from "react";
import { isBrowser } from "react-device-detect";
import { Redirect, Route, Switch } from "react-router-dom";

import { GlobalModal } from "../components/molecules/GlobalModal";
import { AccountBar } from "../components/organisms/AccountBar";
import { Navbar } from "../components/organisms/navbar/Navbar";
import { UserContext } from "../contexts/UserContext";
import { Affiliate } from "../pages/Affiliate";
import { CoinJarCallback } from "../pages/coinJar/CoinJarCallback";
import { Dashboard } from "../pages/Dashboard";
import { DataSources } from "../pages/DataSources";
import { FirstDataSource } from "../pages/FirstDataSource";
import { FirstDataSourceChild } from "../pages/FirstDataSourceChild";
import { FirstDataSourceCustom } from "../pages/FirstDataSourceCustom";
import { Plans } from "../pages/Plans";
import { Settings } from "../pages/Settings";
import { TaxReports } from "../pages/TaxReports";
import { Transactions } from "../pages/Transactions";
import { renderRoutedComponentWithFallback } from "./renderRoutedComponentWithFallback";
import { LoggedOutPageContainer } from "./Unauthenticated";

export const Authenticated = (): JSX.Element => {
  const { authenticated } = useContext(UserContext);

  // if not !authenticated then some queries that hit authenticated routes will fail
  // so delay loading these routes till auth is in place
  // return temp element to prevent default route from running
  if (!authenticated) return <></>;

  return <Router />;
};

const LoggedInPageContainer = ({
  children,
}: {
  children?: React.ReactNode;
}) => (
  <Stack
    h="100%"
    w="100%"
    overflow="hidden"
    spacing={0}
    direction={isBrowser ? { base: "column", xs: "row" } : "column"}
  >
    <GlobalModal />

    <Navbar />

    <VStack
      direction="column"
      bgColor="white.100"
      overflowX="hidden"
      overflowY="auto"
      flexGrow={1}
      spacing={0}
      alignItems="left"
    >
      <AccountBar />
      {children}
    </VStack>
  </Stack>
);

const SentryRoute = Sentry.withSentryRouting(Route);

const Router = () => {
  const { firstLogin } = useContext(UserContext);
  return (
    <Switch>
      <SentryRoute
        exact
        path="/dashboard"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <Dashboard />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/data-sources"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <DataSources />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/transactions"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <Transactions />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/tax-reports"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <TaxReports />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/settings"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <Settings />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/plans"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <Plans />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/affiliate"
        render={renderRoutedComponentWithFallback(() => (
          <LoggedInPageContainer>
            <Affiliate />
          </LoggedInPageContainer>
        ))}
      ></SentryRoute>
      <SentryRoute
        exact
        path="/data-sources/coinjar-callback"
        render={renderRoutedComponentWithFallback(() => (
          <CoinJarCallback />
        ))}
      ></SentryRoute>
      <SentryRoute path="/first-custom-data-source/:customSourceName">
        <LoggedOutPageContainer>
          <FirstDataSourceCustom />
        </LoggedOutPageContainer>
      </SentryRoute>
      <SentryRoute path="/first-custom-data-source/">
        <LoggedOutPageContainer>
          <FirstDataSourceCustom />
        </LoggedOutPageContainer>
      </SentryRoute>
      <SentryRoute path="/first-data-source/:sourceId">
        <LoggedOutPageContainer>
          <FirstDataSourceChild />
        </LoggedOutPageContainer>
      </SentryRoute>

      <SentryRoute exact path="/first-data-source">
        <LoggedOutPageContainer>
          <FirstDataSource />
        </LoggedOutPageContainer>
      </SentryRoute>
      <SentryRoute>
        <Redirect to={firstLogin ? "/first-data-source" : "/dashboard"} />
      </SentryRoute>
    </Switch>
  );
};
